/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2025.
 */

import { SEO_ESCAPE_CHAR } from '@/data/constants/seo';

const markAsObfuscated = (o: any) => {
	if (Array.isArray(o)) {
		o.forEach((item) => markAsObfuscated(item));
	} else if (o && typeof o === 'object') {
		Object.assign(o, { _obfuscated: true });
	}
};

const markAsDeObfuscated = (o: any) => {
	if (Array.isArray(o)) {
		o.forEach((item) => markAsDeObfuscated(item));
	} else if (o && typeof o === 'object') {
		Object.assign(o, { _obfuscated: false });
	}
};

/**
 * Helper: Insert invisible char to break URL pattern
 */
const escapeString = (value: string): string => {
	// Only process if it looks like a path or URL to save cycles
	if (!value.startsWith('/') && !value.startsWith('http')) return value;
	return value.slice(0, 1) + SEO_ESCAPE_CHAR + value.slice(1);
};

/**
 * Helper: Remove invisible char
 */
const unescapeString = (value: string): string =>
	value.replace(new RegExp(SEO_ESCAPE_CHAR, 'g'), '');

const processTargetValue = <T>(currentValue: T, processor: (str: string) => string): T => {
	if (typeof currentValue === 'string') {
		return processor(currentValue) as unknown as T;
	} else if (Array.isArray(currentValue)) {
		// Special handling if the target is an array of strings (e.g. gallery images)
		const newArray = currentValue.map((item) =>
			typeof item === 'string' ? processor(item) : item
		);
		// Simple comparison to see if strings changed
		const isDifferent = newArray.some((val, i) => val !== currentValue[i]);
		return isDifferent ? (newArray as unknown as T) : currentValue;
	} else {
		return currentValue;
	}
};

/**
 * Immutable Processor (Copy-on-Write)
 */
// eslint-disable-next-line complexity
const processImmutable = <T>(
	node: T,
	targetKeys: Set<string>,
	processor: (str: string) => string
): T => {
	// 1. Primitive Check: Return immediately
	if (!node || typeof node !== 'object') {
		return node;
	}

	// 2. Handle Arrays
	if (Array.isArray(node)) {
		let hasChanged = false;

		// Map potentially returns a new array structure
		const newArray = node.map((item) => {
			const result = processImmutable(item, targetKeys, processor);
			if (result !== item) hasChanged = true;
			return result;
		});

		// If nothing inside changed, return the original array reference
		// to prevent unnecessary React re-renders.
		return hasChanged ? (newArray as unknown as T) : node;
	}

	// 3. Handle Objects
	let hasChanged = false;
	// Create a potential copy (we won't use it unless we find a change)
	const newObject = { ...node } as any;

	// eslint-disable-next-line functional/no-loop-statement
	for (const key in node) {
		if (Object.prototype.hasOwnProperty.call(node, key)) {
			const currentValue = (node as any)[key];
			const isTarget = targetKeys.has(key);

			// Case A: This specific key is targeted
			if (isTarget) {
				const newValue = processTargetValue(currentValue, processor);
				if (newValue !== currentValue) {
					newObject[key] = newValue;
					hasChanged = true;
				}
			} else if (typeof currentValue === 'object' && currentValue !== null) {
				// Case B: Not a target key, but we must recurse deeper
				const newValue = processImmutable(currentValue, targetKeys, processor);
				if (newValue !== currentValue) {
					newObject[key] = newValue;
					hasChanged = true;
				}
			}
		}
	}

	return hasChanged ? newObject : node;
};

/**
 * The data inside the next_data script tag contains some URL-like strings, especially in the
 * fallback data for SWR, which causes crawlers to crawl them. However, not all of those URL-like
 * strings are valid URLs, and that results in invalid entries.
 *
 * To prevent crawlers from crawling those invalid URLs, we obfuscate those URL-like strings by
 * inserting invisible characters.
 * Later, when we need to use those strings, we deobfuscate them by removing the invisible characters.
 */
export const obfuscate = <T>(data: T, keys: string[]): T => {
	const result = processImmutable(data, new Set(keys), escapeString);
	markAsObfuscated(result);
	return result;
};

export const deobfuscate = <T>(data: T, keys: string[]): T => {
	const result = processImmutable(data, new Set(keys), unescapeString);
	markAsDeObfuscated(result);
	return result;
};

export const isObfuscated = (data: any): boolean => {
	if (Array.isArray(data)) {
		// only need to check first item in array
		return isObfuscated(data.at(0));
	} else if (data && typeof data === 'object') {
		return !!data._obfuscated;
	} else {
		return false;
	}
};
